import React, { useEffect, useState } from "react";
import { Loader2, CheckCircle, XCircle } from "lucide-react";

/**
 * Handles the redirect from Outlook OAuth connector.
 * Expected URL params: ?status=success or ?outlook_status=success
 */
const OutlookAuthHandler: React.FC = () => {
  const [authStatus, setAuthStatus] = useState<{
    status: string | null;
    error: string | null;
  }>({ status: null, error: null });

  useEffect(() => {
    const closeWindow = (delay: number = 2000) => {
      setTimeout(() => window.close(), delay);
    };

    try {
      const urlParams = new URLSearchParams(window.location.search);
      // Support both general and specific status parameters
      const status = urlParams.get("status") || urlParams.get("outlook_status") || urlParams.get("one_drive_status");
      const error = urlParams.get("error");
      
      setAuthStatus({ status, error });

      if (status === "success") {
        if (window.opener) {
          window.opener.postMessage(
            {
              type: "OUTLOOK_AUTH_SUCCESS",
              status: "success",
              timestamp: Date.now(),
            },
            "*"
          );
        }

        closeWindow(2000);
      } else {
        if (window.opener) {
          window.opener.postMessage(
            {
              type: "OUTLOOK_AUTH_ERROR",
              error: error || "Authentication failed",
              timestamp: Date.now(),
            },
            "*"
          );
        }
      }
    } catch (err) {
      console.error("Outlook Auth handler error:", err);
      if (window.opener) {
        window.opener.postMessage(
          {
            type: "OUTLOOK_AUTH_ERROR",
            error: "Failed to process authentication callback",
            timestamp: Date.now(),
          },
          "*"
        );
      }
    }
  }, []);

  const { status, error } = authStatus;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white rounded-xl border border-gray-100 p-8 max-w-sm w-full text-center shadow-lg">
        {status === "success" ? (
          <div className="animate-in fade-in zoom-in duration-300">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Connected to Outlook
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              Your account has been successfully linked. You can close this
              window now.
            </p>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
              <Loader2 className="w-3 h-3 animate-spin" />
              Closing window...
            </div>
          </div>
        ) : (
          <div className="animate-in fade-in zoom-in duration-300">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Connection Failed
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              {error || "Unable to complete the authentication process."}
            </p>
            <button
              onClick={() => window.close()}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
            >
              Close Window
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OutlookAuthHandler;
